import React, { FC, Fragment, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Table, TableBody, TableContainer, TableHead, TableRow, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DataTableHeadCell } from 'components/shared/data-table/data-table.componet';
import { MaterialPriceList } from 'models/material';
import { MaterialMacro } from 'models/material-macro';
import MaterialDataGridRow from './materials-data-grid-row.component';
import { MaterialGridInput } from 'models/order-input/shared';
import isEqual from 'lodash/isEqual';
import { emptyMaterialInput, isMaterialRowEmpty } from './form-helper';
import { OrderDetailsContext } from 'pages/order-details/order-details-context';
import { useMaterialMacros } from 'hooks/useMaterialMacros';
import { createMacroMaterialInputs, toMaterialMacroOptions } from '../actions-and-materials-helper';
import UseMaterialMacroButton from 'components/materials/use-material-macro-dialog/use-material-macro-button.component';

// Quantity for rows added through the "Use macro" button. A macro carries no quantity of its
// own and, unlike the action-linked path, there is no action here to inherit one from.
const USE_MACRO_MATERIAL_QUANTITY = '1';

type MaterialsDataGridProps = {
    materials: MaterialGridInput[];
    priceList: MaterialPriceList;
    onChange?: (materials: MaterialGridInput[], isGridValid: boolean) => void;
    warranty?: boolean;
    // Shows the "Use macro" button under the table (hidden again while the lab has no macros).
    allowMaterialMacros?: boolean;
};
const MaterialsDataGrid: FC<MaterialsDataGridProps> = ({
    materials,
    priceList,
    onChange,
    warranty,
    allowMaterialMacros,
}) => {
    const { t: translate } = useTranslation(['common', 'orders', 'actions-materials']);
    const [materialStates, setMaterialStates] = useState<MaterialGridInput[]>([]);
    // The rows the grid last committed. handleRowChange reads and writes it synchronously, so
    // the parent's onChange never runs inside a setState updater: React replays queued
    // updaters while rendering, and a parent update from there looped until "Maximum update
    // depth exceeded" (typing a free-text line into the lab order grid did it).
    const materialStatesRef = useRef<MaterialGridInput[]>([]);
    const commitMaterialStates = (rows: MaterialGridInput[]) => {
        materialStatesRef.current = rows;
        setMaterialStates(rows);
    };
    const { order } = useContext(OrderDetailsContext);
    const { materialMacros, isLoading: isMaterialMacrosLoading } = useMaterialMacros(!allowMaterialMacros);
    const materialMacroOptions = useMemo(
        () => toMaterialMacroOptions(materialMacros, priceList?.materials || []),
        [materialMacros, priceList?.materials],
    );

    useEffect(() => {
        commitMaterialStates([]);
    }, [order?._id]);

    useEffect(() => {
        commitMaterialStates([...materials, { ...emptyMaterialInput() }]);
    }, [materials]);

    const handleRowChange = (index: number, material: MaterialGridInput) => {
        const prev = materialStatesRef.current;
        // Prevent unnecessary updates if nothing changed
        const prevRow = prev[index];
        if (prevRow && isEqual(prevRow, material)) {
            return;
        }
        let updated = [...prev];
        updated[index] = material;
        const isLastRow = index === prev.length - 1;
        if (isLastRow && !isMaterialRowEmpty(material)) {
            updated = [...updated, { ...emptyMaterialInput() }];
        }

        if (updated.length > 1) {
            // Remove empty rows
            updated = updated.filter((row, idx) => idx === updated.length - 1 || !isMaterialRowEmpty(row));
            if (updated.length === 0) {
                updated = [emptyMaterialInput()];
            }
        }

        commitMaterialStates(updated);
        if (onChange) {
            const noneEmptyRows = updated.slice(0, -1).filter((row) => row && !isMaterialRowEmpty(row));
            onChange(
                noneEmptyRows,
                noneEmptyRows.every((row) => row.isValid),
            );
        }
    };

    // Same onChange contract as handleRowChange (non-empty rows + aggregate validity). The
    // parents feed that output straight back in as `materials`, which rebuilds the local
    // rows including the trailing placeholder.
    const handleMacroMaterialsAdded = (materialMacro: MaterialMacro) => {
        const macroRows = createMacroMaterialInputs(
            materialMacro,
            priceList?.materials || [],
            USE_MACRO_MATERIAL_QUANTITY,
        );
        if (macroRows.length === 0) {
            return;
        }
        const existingRows = materialStatesRef.current.filter((row) => !isMaterialRowEmpty(row));
        const updated = [...existingRows, ...macroRows, emptyMaterialInput()];
        commitMaterialStates(updated);
        if (onChange) {
            const noneEmptyRows = updated.slice(0, -1);
            onChange(
                noneEmptyRows,
                noneEmptyRows.every((row) => row.isValid),
            );
        }
    };

    return (
        <Fragment>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <DataTableHeadCell sx={{ width: 100 }}>
                                {translate('actions-materials:MATERIAL')}
                            </DataTableHeadCell>
                            <DataTableHeadCell>{translate('common:DESCRIPTION')}</DataTableHeadCell>
                            <DataTableHeadCell sx={{ width: 150 }}>
                                {translate('orders:BATCH_NUMBER')}
                            </DataTableHeadCell>
                            <DataTableHeadCell sx={{ width: 70 }} alignRight>
                                {translate('orders:QUANTITY')}
                            </DataTableHeadCell>
                            <DataTableHeadCell sx={{ width: 90 }} alignRight>
                                {translate('orders:UNIT_PRICE')}
                            </DataTableHeadCell>
                            <Tooltip title={translate('TOTAL_EXCL_VAT').toString()} placement="top">
                                <DataTableHeadCell sx={{ width: 120 }} alignRight>
                                    {translate('orders:TOTAL_EXCL_VAT')}
                                </DataTableHeadCell>
                            </Tooltip>
                            <DataTableHeadCell sx={{ width: 80 }} alignRight>
                                {translate('orders:VAT_PERCENT')}
                            </DataTableHeadCell>
                            <Tooltip title={translate('TOTAL_INCL_VAT').toString()} placement="top">
                                <DataTableHeadCell sx={{ width: 120 }} align="right">
                                    {translate('orders:TOTAL_INCL_VAT')}
                                </DataTableHeadCell>
                            </Tooltip>
                            <DataTableHeadCell sx={{ width: 61 }} />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {materialStates.map((row, index) => {
                            const isLast = index === materialStates.length - 1;
                            const isPlaceholderRow = isLast && isMaterialRowEmpty(row);
                            return (
                                <MaterialDataGridRow
                                    key={index}
                                    material={row}
                                    priceList={priceList}
                                    isPlaceholderRow={isPlaceholderRow}
                                    onChange={(material) => handleRowChange(index, material)}
                                    warranty={warranty}
                                    dataTest={`materials-data-grid.row-${index}`}
                                />
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            {allowMaterialMacros ? (
                <Box mt={1}>
                    <UseMaterialMacroButton
                        materialMacroOptions={materialMacroOptions}
                        onAddMaterialMacro={handleMacroMaterialsAdded}
                        isLoading={isMaterialMacrosLoading}
                        // Until the price list is in, every macro would read as "materials missing".
                        disabled={!priceList?.materials?.length}
                        dataTest="materials-data-grid.use-macro-btn"
                    />
                </Box>
            ) : undefined}
        </Fragment>
    );
};

export default MaterialsDataGrid;
